
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import React, { useEffect } from "react";

export default function DialerPage() {
  useEffect(() => {
    const reveals = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    if (!reveals.length) return;

    // Respect reduced motion
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      reveals.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          // ✅ Fade in when visible, fade out when not visible
          if (entry.isIntersecting) el.classList.add("is-visible");
          else el.classList.remove("is-visible");
        });
      },
      {
        threshold: 0.15,
        rootMargin: "0px 0px -10% 0px",
      }
    );

    reveals.forEach((el) => observer.observe(el));

    // ✅ First load visible items
    reveals.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) el.classList.add("is-visible");
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "'Roboto', sans-serif" }}>
      {/* ✅ Only animation CSS added. No design/content changes */}
      <style>{`
        .reveal {
          opacity: 0;
          transform: translate3d(0, 18px, 0);
          filter: blur(6px);
          transition: opacity 700ms ease, transform 700ms ease, filter 700ms ease;
          will-change: opacity, transform, filter;
        }
        .reveal.is-visible {
          opacity: 1;
          transform: translate3d(0, 0, 0);
          filter: blur(0px);
        }
        @media (prefers-reduced-motion: reduce) {
          .reveal,
          .reveal.is-visible {
            transition: none !important;
            transform: none !important;
            filter: none !important;
            opacity: 1 !important;
          }
        }
      `}</style>

      <Navigation />

      {/* HERO SECTION - DARK THEME */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#040814] text-white py-24">
        {/* BACKGROUND ELEMENTS */}
        <div className="absolute inset-0"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* LEFT CONTENT */}
            <div className="space-y-8 reveal">
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-800/30 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
                <span className="text-sm font-medium text-cyan-300">Call Center Technology</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                What is a{" "}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  Dialer
                </span>
                ?
              </h1>

              <p className="text-base text-gray-300 leading-relaxed max-w-2xl">
                A call center dialer is an application that automates outbound calls. It comes in different types – all
                configurable and usually integrated with your CRM system. A call center dialer is one of the best tools
                to have when you make a lot of outbound calls. It is not just a dialer for call centers. It can
                potentially improve the productivity of your agent or salesperson. If you’re a start-up with only a
                handful of employees or a big operation, you can benefit from using a call center dialer. The key is to
                choose the best type of dialer for your needs.
              </p>

              {/* SMALLER BUTTONS */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <a
                  href="#capabilities"
                  className="group relative px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-cyan-500/25 inline-flex items-center justify-center"
                >
                  <span>Explore Capabilities</span>
                  <svg
                    className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </a>

                <a
                  href="#how"
                  className="px-6 py-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 text-white text-sm font-semibold hover:bg-white/10 transition-all duration-300 inline-flex items-center justify-center"
                >
                  <span>See How It Works</span>
                </a>
              </div>
            </div>

            {/* RIGHT IMAGE */}
            <div className="relative reveal">
              {/* WHITE IMAGE BOX ONLY */}
              <div className="relative rounded-3xl bg-white p-4 shadow-2xl shadow-cyan-500/10">
                <div className="aspect-video">
                  <img
                    src="/dialingprocess.png"
                    alt="Dialer Interface"
                    className="w-full h-full object-contain rounded-2xl transform hover:scale-[1.02] transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES SECTION - LIGHT THEME */}
      <section id="capabilities" className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, #0891b2 1px, transparent 0)`,
              backgroundSize: "40px 40px",
            }}
          ></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-14 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-100 mb-5">
              <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
              <span className="text-sm font-medium text-cyan-700">Powerful Features</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Advanced{" "}
              <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">
                Features & Capabilities
              </span>
            </h2>

            <p className="text-base text-gray-600">
              Discover the comprehensive suite of features that make our dialer software the most powerful tool for
              modern call centers
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="reveal group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-t-2xl"></div>

                <div className="relative">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>

                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION - DARK THEME */}
      <section id="how" className="py-20 bg-gradient-to-b from-gray-900 to-gray-800 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            {/* LEFT SIDE TEXT */}
            <div className="space-y-6 reveal">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border border-cyan-800/40 backdrop-blur-sm">
                <span className="text-xs font-medium text-cyan-300">Process Flow</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                How Our{" "}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  Dialer Works
                </span>
              </h2>

              <p className="text-base text-gray-300 leading-relaxed">
                Dialers collect a large amount of information about the agents handling calls and place outbound calls
                just before the agent become available. They automatically dial several contacts at the same time until
                one answers; and then transfers the call to the most appropriate agent.
              </p>

              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-sm font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-base mb-1.5">Smart Contact Prediction</h4>
                    <p className="text-gray-400 text-sm">System predicts the most effective time to connect</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-r from-sky-500 to-indigo-600 flex items-center justify-center text-sm font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-base mb-1.5">Intelligent Routing</h4>
                    <p className="text-gray-400 text-sm">Calls automatically route to the best available agent</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-sm font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-base mb-1.5">Real-time Analytics</h4>
                    <p className="text-gray-400 text-sm">Monitor and improve performance instantly</p>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE IMAGE */}
            <div className="relative reveal">
              <div className="rounded-3xl overflow-hidden shadow-2xl bg-white p-4">
                <img src="/Dialerwork.png" alt="Dialer Workflow" className="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIALER TYPES SECTION - LIGHT THEME */}
      <section id="types" className="py-16 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* SECTION HEADER */}
          <div className="text-center max-w-3xl mx-auto mb-14 reveal">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 mb-4">
              <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
              <span className="text-xs font-medium text-cyan-700">Dialer Types</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose Your{" "}
              <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">
                Dialing Strategy
              </span>
            </h2>

            <p className="text-base text-gray-600">
              Select from our range of dialer solutions designed to fit different business needs and boost your calling
              efficiency.
            </p>
          </div>

          {/* CARDS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {dialerTypes.map((type, index) => (
              <div
                key={index}
                className="reveal group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200"
              >
                <div className="relative z-10">
                  {/* ICON + TITLE */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      {type.icon}
                    </div>

                    <h3 className="text-lg font-bold text-gray-900">{type.title}</h3>
                  </div>

                  {/* DESCRIPTION */}
                  <p className="text-sm text-gray-600 leading-relaxed mb-5">{type.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Features Data
const features = [
  {
    icon: (
      <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
    title: "Contact preview",
    description:
      "Users can view information about the contact they’re calling. Aggregated contact details pop-up to provide context for the sales call.",
  },
  {
    icon: (
      <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
        />
      </svg>
    ),
    title: "Dialer-CRM integration",
    description:
      "Dialer integrates with CRM systems to pull in contact information and automatically log call details into CRM records.",
  },
  {
    icon: (
      <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
    ),
    title: "Built-in CRM",
    description: "Dialer includes built-in CRM capabilities to manage contacts, calls, and communication history in one place.",
  },
  {
    icon: (
      <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
        />
      </svg>
    ),
    title: "Social media integration",
    description: "Dialer connects with social platforms to pull background information and enrich contact profiles.",
  },
  {
    icon: (
      <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),
    title: "SI database integrations",
    description: "Dialer integrates with third-party databases to provide deeper sales intelligence and enriched contact data.",
  },
  {
    icon: (
      <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: "Notes & tags",
    description: "Users can take notes and create tags during conversations to organize and prioritize contacts.",
  },
];

// Dialer Types Data
const dialerTypes = [
  {
    icon: (
      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
        />
      </svg>
    ),
    title: "Manual Dialing",
    description:
      "Perfect for businesses starting their journey. Provides agents with complete autonomy over calling decisions while maintaining personal touch and responsibility.",
  },
  {
    icon: (
      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
        />
      </svg>
    ),
    title: "Preview Dialer",
    description:
      "Agents review comprehensive prospect information before initiating calls, ensuring fully prepared and contextually relevant conversations for sensitive engagements.",
  },
  {
    icon: (
      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
    ),
    title: "Click-to-Call",
    description:
      "Direct CRM integration allows instant calling with full context. While methodical, it ensures every conversation is informed and productive with automatic data capture.",
  },
  {
    icon: (
      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: "Predictive Dialer",
    description:
      "Advanced AI algorithms predict agent availability and optimize call timing, dramatically reducing idle time and maximizing productivity for high-volume operations.",
  },
];
